/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {BUSINESS_PLAN, DISMISS_MSG, VEHICLE_COLUMN_HEADERS} from '../../../common/constants';
import {
  TableDisplayConfirmationPopUpComponent
} from '../../../core/components/pop-ups/table-display-confirmation-pop-up/table-display-confirmation-pop-up.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {AuditGsegResults} from '../../../models/audit-gseg-results.model';
import {ProgramCode} from '../../../models/program-code.model';
import {VehiclePortfolioLoadResult} from '../../../models/vehicle-portfolio-load-result.model';
import {VehiclePortfolioLoad} from '../../../models/vehicle-portfolio-load.model';
import {VehiclePortfolioLoadService} from '../../../services/vehicle-portfolio-load.service';
import {RESULT_TABS} from '../vehicle-portfolio-load/vehicle-portfolio-load.component';

@Component({
    selector: 'app-vehicle-portfolio-transfer',
    templateUrl: './vehicle-portfolio-transfer.component.html',
    styleUrl: './vehicle-portfolio-transfer.component.scss',
    standalone: false
})
export class VehiclePortfolioTransferComponent implements OnInit {
  showSpinner: boolean;
  selectedProgramCodes: any[] = [];
  prgmCodeList: ProgramCode[];
  vehiclePortfolio: VehiclePortfolioLoad[];
  recordsToTransfer: VehiclePortfolioLoad[];

  colDefinition: ColDef[] = [
    {field: 'arch', headerName: 'Architecture', filter: AutocompleteFilterComponent, lockVisible: true},
    {field: 'prgmCode', headerName: 'Program Code', filter: AutocompleteFilterComponent, lockVisible: true},
  ];

  gsegColumnHeaders: ColumnHeader[] = [
    {name: 'sevenKeyFields.plngMkt', label: 'PLNGMKT'},
    {name: 'sevenKeyFields.mktDiv', label: 'BRAND'},
    {name: 'sevenKeyFields.brand', label: 'LBRAND'},
    {name: 'sevenKeyFields.lbrand', label: 'MKTDIV'},
    {name: 'sevenKeyFields.prgmCode', label: 'PRGMCODE'},
    {name: 'sevenKeyFields.styleCode', label: 'BODYSTYLE'},
    {name: 'sevenKeyFields.bodyStyle', label: 'STYLECODE'},
    {name: 'businessPlanGseg', label: 'BP_GSEG'},
    {name: 'tvpGseg', label: 'TVP_GSEG'},
    {name: 'brandfamGseg', label: 'BRANDFAM_GSEG'}
  ];

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    selectAll: "filtered",
    enableClickSelection: true,
    enableSelectionWithoutKeys: true
  };

  constructor(public readonly vehPortfolioService: VehiclePortfolioLoadService,
              public readonly snackBar: FabMessageService,
              readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.getProgramCodesFromVehPortfolio();
  }

  /**
   * Get the program codes from the current vehicle portfolio
   */
  getProgramCodesFromVehPortfolio(): void {
    this.prgmCodeList = [];
    this.showSpinner = true;
    this.vehPortfolioService.getCurrentPortfolioByPortType(BUSINESS_PLAN).subscribe((data: any) => {
      if (data) {
        this.vehiclePortfolio = data;
        // Add unique program codes from the vehicle portfolio to the program code list
        this.prgmCodeList = data
        .filter((item, i, arr) => arr.findIndex((e) => e.sevenKeyFields.prgmCode === item.sevenKeyFields.prgmCode) === i)
        .map((row) => {
          return {prgmCode: row.sevenKeyFields.prgmCode, arch: row.arch}
        })
        .sort((a, b) => a.prgmCode.localeCompare(b.prgmCode));
      }
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      this.showSpinner = false;
    });
  }

  /**
   * Event handler for selecting a program code
   * @param event the clicked row
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedProgramCodes.push(event.data);
    } else {
      this.selectedProgramCodes = this.selectedProgramCodes.filter(element => element != event.data);
    }
  }

  /**
   * Event handler for clicking the transfer button
   */
  onTransferClick(): void {
    // Get all records from the business portfolio that have the selected program codes
    this.recordsToTransfer = this.vehiclePortfolio.filter((record) => {
      return this.selectedProgramCodes.some((selected) => selected.prgmCode === record.sevenKeyFields.prgmCode);
    });

    const dialogRef = this.dialog.open(TableDisplayConfirmationPopUpComponent, {
      width: '70vw',
      data: {
        title: 'Confirmation',
        message: 'This will update the TVP Portfolio with the selected program codes from the Business Plan portfolio. Do you want to proceed?',
        colDefinition: this.colDefinition,
        tableData: this.selectedProgramCodes
      },
      disableClose: true
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.updateTVPPortfolio();
      }
    });
  }

  /**
   * Update the TVP Portfolio with the selected records from the Business Plan portfolio and export the results to Excel
   */
  updateTVPPortfolio(): void {
    this.showSpinner = true;
    this.vehPortfolioService.businessPortfolioTransfer(this.recordsToTransfer).subscribe((data: any) => {
      this.exportToExcel(data['Business Portfolio Transfer']);
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      this.showSpinner = false
      this.snackBar.displaySuccess('The selected program codes were transferred successfully.', DISMISS_MSG)
    });
  }

  /**
   * Export the results of the business portfolio transfer to Excel
   * @param loadResults the results of the business portfolio transfer
   */
  exportToExcel(loadResults: VehiclePortfolioLoadResult): void {
    // Create the sheet list for the Excel export
    const sheetList: [string, ColumnHeader[]][] = [
      ['New Portfolio', VEHICLE_COLUMN_HEADERS],
      ['Old Portfolio', VEHICLE_COLUMN_HEADERS],
      ['Gseg Audit', this.gsegColumnHeaders],
      ['Rejects', VEHICLE_COLUMN_HEADERS],
      ['Changed Records', VEHICLE_COLUMN_HEADERS],
      ['Deleted Records', VEHICLE_COLUMN_HEADERS],
      ['New Records', VEHICLE_COLUMN_HEADERS]
    ];
    // Create the data map that contains what data should go in each sheet
    const data = new Map<string, VehiclePortfolioLoad[] | AuditGsegResults[]>();
    data[RESULT_TABS.NEW_PORTFOLIO] = loadResults.newPortfolio;
    data[RESULT_TABS.OLD_PORTFOLIO] = loadResults.oldPortfolio;
    data[RESULT_TABS.GSEG_AUDIT] = loadResults.gsegAudit;
    data[RESULT_TABS.REJECTS] = loadResults.rejects;
    data[RESULT_TABS.CHANGED] = loadResults.changedRecords;
    data[RESULT_TABS.DELETED] = loadResults.deletedRecords;
    data[RESULT_TABS.NEW] = loadResults.newRecords;

    // Export the results of the business portfolio transfer
    const currDate = new Date().toISOString();
    const worksheetExportRequest = this.vehPortfolioService.createExportRequestBody('ResultsVehPortTransfer_' + currDate,
      data,
      sheetList);

    this.showSpinner = true;
    if (worksheetExportRequest === undefined) {
      this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
      this.showSpinner = false;
    } else {
      this.vehPortfolioService.transformToExcel(worksheetExportRequest).subscribe(
        (file: any) => {
          this.vehPortfolioService.exportToExcelFromServer(file, worksheetExportRequest.fileName);
        },
        () => {
          this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
          this.showSpinner = false;
        }, () => {
          this.showSpinner = false;
        });
    }
  }
}
